import { Avatar, type AvatarProps } from '@/components/ui/avatar';
import { getInitialsFromName } from '@/components/ui/avatar-initials';
import { getStaffPhotoUrl } from '@/data/staffPlaceholderPhotos';
import { useStaffPhotoUrl } from '@/hooks/useStaffPhotoUrl';
import { cn } from '@/lib/utils';

type StaffAvatarProps = Omit<AvatarProps, 'initials' | 'alt'> & {
  staffId?: string;
  firstName?: string;
  lastName?: string;
  displayName: string;
  photoUrl?: string | null;
  photoStoragePath?: string | null;
  src?: string | null;
  /** @deprecated Use consistent foreground avatars; ignored for Airbnb-style UI */
  color?: string;
};

export function StaffAvatar({
  staffId,
  firstName,
  lastName,
  displayName,
  photoUrl,
  photoStoragePath,
  src,
  size = 'md',
  className,
}: StaffAvatarProps) {
  const initials = getInitialsFromName(firstName, lastName, displayName);
  const shouldResolveFromStorage = src === undefined && !photoUrl && Boolean(photoStoragePath);
  const storagePhotoQuery = useStaffPhotoUrl(
    shouldResolveFromStorage ? (photoStoragePath ?? undefined) : undefined,
  );
  const resolvedSrc =
    src ??
    photoUrl ??
    storagePhotoQuery.data ??
    getStaffPhotoUrl({
      id: staffId ?? displayName,
      displayName,
      photoUrl: null,
    });

  return (
    <Avatar
      alt={displayName}
      className={cn(className)}
      initials={initials}
      size={size}
      src={resolvedSrc}
    />
  );
}
